"use client";

import { cn } from "@repo/ui/lib/cn";
import { useReducedMotion } from "motion/react";
import Image from "next/image";
import { type CSSProperties, type ReactNode, useLayoutEffect } from "react";

/**
 * One star placement in a {@link SparkleStars} canvas.
 * Coordinates are percentages of the layout canvas (same space as
 * `shine-stars.png` bboxes when using {@link SPARKLE_STARS_LEVEL_UP_BAND}).
 */
export interface SparkleStarPlacement {
	id: string;
	/** Center X, 0–100 (% of canvas width). */
	cx: number;
	/** Center Y, 0–100 (% of canvas height). */
	cy: number;
	/** Box width, % of canvas width. */
	w: number;
	/** Box height, % of canvas height. */
	h: number;
	/** Optional per-star stagger override (seconds). */
	delaySec?: number;
}

export interface SparkleStarsMotion {
	/** Full sparkle cycle length in seconds. */
	cycleSec?: number;
	/** Scale at trough (rest / contracted). */
	scaleMin?: number;
	/** Scale at peak (expanded). */
	scaleMax?: number;
	/** Opacity at trough. */
	opacityMin?: number;
	/** Opacity at peak. */
	opacityMax?: number;
}

export interface SparkleStarsProps extends SparkleStarsMotion {
	className?: string;
	/**
	 * Size/position shell. Defaults to a centered absolute fill suitable for
	 * layering behind heroes / chests (`pointer-events-none`).
	 */
	frameClassName?: string;
	/** Classes on the aspect-ratio canvas that owns % placements. */
	canvasClassName?: string;
	/**
	 * Named layout. Ignored when {@link stars} is provided.
	 * - `levelUpBand` — left/right clusters from `shine-stars.png`
	 * - `halo` — ellipse around centre (loot chest, badges, etc.)
	 */
	layout?: "levelUpBand" | "halo";
	/** Explicit placements (highest priority). */
	stars?: readonly SparkleStarPlacement[];
	/** Halo star count when `layout="halo"`. Default 8. */
	haloCount?: number;
	/** Halo ellipse radii as % of canvas. Defaults 42 / 36. */
	haloRadiusX?: number;
	haloRadiusY?: number;
	/** Halo star box size as % of canvas. Default ~5.5 / 12. */
	haloStarW?: number;
	haloStarH?: number;
	/**
	 * Canvas aspect (width/height). Level-up band matches `shine-stars.png`
	 * (2096/932). Halo defaults to 1 (square).
	 */
	aspectRatio?: number;
	/** Per-star image. Default `/images/shine-star.png`. */
	starSrc?: string;
	starImageWidth?: number;
	starImageHeight?: number;
	/** Radial expand origin as % of canvas. Default centre (50, 50). */
	originCx?: number;
	originCy?: number;
	/** Freeze motion (still renders stars). */
	paused?: boolean;
	children?: ReactNode;
}

/** Measured bboxes from `shine-stars.png` (2096×932). */
export const SPARKLE_STARS_LEVEL_UP_BAND: readonly SparkleStarPlacement[] = [
	{ id: "l0", cx: 1.57, cy: 91.26, w: 2.53, h: 5.58 },
	{ id: "l1", cx: 5.2, cy: 11.86, w: 2.53, h: 5.58 },
	{ id: "l2", cx: 5.87, cy: 63.04, w: 4.15, h: 9.23 },
	{ id: "l3", cx: 10.64, cy: 31.55, w: 3.48, h: 7.83 },
	{ id: "l4", cx: 16.79, cy: 86.16, w: 5.68, h: 12.55 },
	{ id: "l5", cx: 23.47, cy: 8.48, w: 5.68, h: 12.55 },
	{ id: "r0", cx: 77.05, cy: 12.23, w: 3.48, h: 7.83 },
	{ id: "r1", cx: 80.1, cy: 95.06, w: 3.48, h: 7.83 },
	{ id: "r2", cx: 87.02, cy: 37.66, w: 5.68, h: 12.55 },
	{ id: "r3", cx: 90.15, cy: 65.5, w: 3.44, h: 7.51 },
	{ id: "r4", cx: 96.99, cy: 16.58, w: 2.53, h: 5.58 },
	{ id: "r5", cx: 98.33, cy: 83.53, w: 2.53, h: 5.58 },
] as const;

const LEVEL_UP_BAND_ASPECT = 2096 / 932;

/** Irregular delays so sparkles never sync as one pulse (seconds). */
const DEFAULT_STAGGER_SEC = [
	0, 0.55, 1.05, 1.7, 2.2, 2.85, 0.35, 1.4, 1.95, 2.5, 3.1, 3.65,
] as const;

const DEFAULT_MOTION: Required<SparkleStarsMotion> = {
	cycleSec: 5.2,
	scaleMin: 0.97,
	scaleMax: 1.03,
	opacityMin: 0.5,
	opacityMax: 1,
};

/**
 * Build an elliptical halo of stars around the canvas centre — useful behind
 * treasure chests, badges, and other focal art.
 */
export function createSparkleStarsHalo(params?: {
	count?: number;
	radiusX?: number;
	radiusY?: number;
	starW?: number;
	starH?: number;
	/** Rotate the first star (radians). Default -π/2 (top). */
	startAngleRad?: number;
}): SparkleStarPlacement[] {
	const count = Math.max(3, Math.floor(params?.count ?? 8));
	const radiusX = params?.radiusX ?? 42;
	const radiusY = params?.radiusY ?? 36;
	const starW = params?.starW ?? 5.5;
	const starH = params?.starH ?? 12;
	const start = params?.startAngleRad ?? -Math.PI / 2;
	const out: SparkleStarPlacement[] = [];
	for (let i = 0; i < count; i++) {
		const t = start + (i / count) * Math.PI * 2;
		out.push({
			id: `halo-${i}`,
			cx: 50 + Math.cos(t) * radiusX,
			cy: 50 + Math.sin(t) * radiusY,
			w: starW * (i % 3 === 0 ? 1.15 : i % 2 === 0 ? 0.85 : 1),
			h: starH * (i % 3 === 0 ? 1.15 : i % 2 === 0 ? 0.85 : 1),
			delaySec: DEFAULT_STAGGER_SEC[i % DEFAULT_STAGGER_SEC.length],
		});
	}
	return out;
}

function sparkleTransformOrigin(
	cxPct: number,
	cyPct: number,
	wPct: number,
	hPct: number,
	originCx: number,
	originCy: number,
): string {
	const leftPct = cxPct - wPct / 2;
	const topPct = cyPct - hPct / 2;
	const originX = ((originCx - leftPct) / wPct) * 100;
	const originY = ((originCy - topPct) / hPct) * 100;
	return `${originX}% ${originY}%`;
}

function motionStyleId(motion: Required<SparkleStarsMotion>): string {
	const key = [
		motion.cycleSec,
		motion.scaleMin,
		motion.scaleMax,
		motion.opacityMin,
		motion.opacityMax,
	].join("-");
	return `sq-sparkle-stars-${key.replace(/\./g, "p")}`;
}

function ensureSparkleStarsStyles(
	motion: Required<SparkleStarsMotion>,
): string {
	const styleId = motionStyleId(motion);
	if (typeof document === "undefined") {
		return styleId;
	}
	if (document.getElementById(styleId)) {
		return styleId;
	}
	// Mid peaks between min/max (same beat structure as the prior composite band).
	const o22 =
		motion.opacityMin + (motion.opacityMax - motion.opacityMin) * 0.7;
	const o68 =
		motion.opacityMin + (motion.opacityMax - motion.opacityMin) * 0.4;
	const o82 =
		motion.opacityMin + (motion.opacityMax - motion.opacityMin) * 0.84;
	const s22 = motion.scaleMin + (motion.scaleMax - motion.scaleMin) * 0.33;
	const s68 = motion.scaleMin + (motion.scaleMax - motion.scaleMin) * 0.25;
	const s82 = motion.scaleMin + (motion.scaleMax - motion.scaleMin) * 0.6;

	const style = document.createElement("style");
	style.id = styleId;
	style.textContent = `
@keyframes ${styleId} {
  0%, 100% {
    opacity: ${motion.opacityMin};
    transform: scale(${motion.scaleMin});
  }
  22% {
    opacity: ${o22};
    transform: scale(${s22});
  }
  45% {
    opacity: ${motion.opacityMax};
    transform: scale(${motion.scaleMax});
  }
  68% {
    opacity: ${o68};
    transform: scale(${s68});
  }
  82% {
    opacity: ${o82};
    transform: scale(${s82});
  }
}
.sq-sparkle-stars-anim[data-sparkle-style="${styleId}"] {
  animation: ${styleId} ${motion.cycleSec}s ease-in-out infinite;
  will-change: opacity, transform;
}
@media (prefers-reduced-motion: reduce) {
  .sq-sparkle-stars-anim[data-sparkle-style="${styleId}"] {
    animation: none;
    opacity: 0.95;
    transform: none;
  }
}
`;
	document.head.appendChild(style);
	return styleId;
}

function resolveStars(props: SparkleStarsProps): {
	stars: readonly SparkleStarPlacement[];
	aspectRatio: number;
} {
	if (props.stars && props.stars.length > 0) {
		return {
			stars: props.stars,
			aspectRatio: props.aspectRatio ?? LEVEL_UP_BAND_ASPECT,
		};
	}
	if (props.layout === "halo") {
		return {
			stars: createSparkleStarsHalo({
				count: props.haloCount,
				radiusX: props.haloRadiusX,
				radiusY: props.haloRadiusY,
				starW: props.haloStarW,
				starH: props.haloStarH,
			}),
			aspectRatio: props.aspectRatio ?? 1,
		};
	}
	return {
		stars: SPARKLE_STARS_LEVEL_UP_BAND,
		aspectRatio: props.aspectRatio ?? LEVEL_UP_BAND_ASPECT,
	};
}

/**
 * Decorative staggered sparkle-star field. Place behind heroes, loot chests,
 * or other celebration surfaces. Uses transform + opacity only; radial
 * expand/contract is via per-star `transform-origin` toward the canvas origin.
 */
export function SparkleStars({
	className,
	frameClassName,
	canvasClassName,
	layout = "levelUpBand",
	stars: starsProp,
	haloCount,
	haloRadiusX,
	haloRadiusY,
	haloStarW,
	haloStarH,
	aspectRatio: aspectRatioProp,
	starSrc = "/images/shine-star.png",
	starImageWidth = 237,
	starImageHeight = 232,
	originCx = 50,
	originCy = 50,
	paused = false,
	cycleSec = DEFAULT_MOTION.cycleSec,
	scaleMin = DEFAULT_MOTION.scaleMin,
	scaleMax = DEFAULT_MOTION.scaleMax,
	opacityMin = DEFAULT_MOTION.opacityMin,
	opacityMax = DEFAULT_MOTION.opacityMax,
	children,
}: SparkleStarsProps) {
	const prefersReducedMotion = useReducedMotion();
	const motion: Required<SparkleStarsMotion> = {
		cycleSec,
		scaleMin,
		scaleMax,
		opacityMin,
		opacityMax,
	};
	const { stars, aspectRatio } = resolveStars({
		layout,
		stars: starsProp,
		haloCount,
		haloRadiusX,
		haloRadiusY,
		haloStarW,
		haloStarH,
		aspectRatio: aspectRatioProp,
	});

	useLayoutEffect(() => {
		ensureSparkleStarsStyles(motion);
	}, [
		motion.cycleSec,
		motion.scaleMin,
		motion.scaleMax,
		motion.opacityMin,
		motion.opacityMax,
	]);

	const styleId = motionStyleId(motion);
	const animate = !paused && !prefersReducedMotion;

	return (
		<div
			className={cn(
				"pointer-events-none absolute inset-0 z-[1] flex items-center justify-center",
				className,
			)}
			aria-hidden
			data-sparkle-stars=""
			data-sparkle-layout={layout}
		>
			<div
				className={cn(
					"relative flex max-w-none items-center justify-center",
					frameClassName,
				)}
			>
				<div
					className={cn(
						"relative h-full w-auto max-w-full",
						canvasClassName,
					)}
					style={{ aspectRatio: String(aspectRatio) }}
				>
					{stars.map((star, index) => {
						const delaySec =
							star.delaySec ??
							DEFAULT_STAGGER_SEC[
								index % DEFAULT_STAGGER_SEC.length
							] ??
							0;
						const starStyle: CSSProperties = {
							left: `${star.cx - star.w / 2}%`,
							top: `${star.cy - star.h / 2}%`,
							width: `${star.w}%`,
							height: `${star.h}%`,
							transformOrigin: sparkleTransformOrigin(
								star.cx,
								star.cy,
								star.w,
								star.h,
								originCx,
								originCy,
							),
							...(animate
								? { animationDelay: `${delaySec}s` }
								: { opacity: 0.95 }),
						};
						return (
							<span
								key={star.id}
								className={cn(
									"absolute block",
									animate && "sq-sparkle-stars-anim",
								)}
								data-sparkle-style={
									animate ? styleId : undefined
								}
								style={starStyle}
							>
								<Image
									src={starSrc}
									alt=""
									width={starImageWidth}
									height={starImageHeight}
									className="pointer-events-none h-full w-full max-w-none object-contain object-center"
									priority={false}
									placeholder="empty"
									unoptimized
								/>
							</span>
						);
					})}
					{children}
				</div>
			</div>
		</div>
	);
}
